import type { InputParam } from "../../../types/grid.ts";
import { useFilterDisplay } from "../../../hooks/useFilterDisplay.ts";
import { CompactVariant } from "./identityVariants/compactVariant.tsx";
import { InlineVariant } from "./identityVariants/inlineVariant.tsx";
import { PaletteVariant } from "./identityVariants/paletteVariant.tsx";

export interface GroupFieldDef {
  label: string;
  /** Property key on the cache item to bucket by. */
  field: string;
  /**
   * Optional computed value extractor. When present, replaces `item[field]`
   * lookup — use for synthetic buckets (e.g. alphabetic first letter) or
   * boolean-to-label mappings ("Active" / "Deleted"). Return an array to place
   * one item in multiple buckets (e.g. a Location under each of its Kiosks'
   * types). Named `getValue` rather than `valueOf` to avoid colliding with
   * Object.prototype.valueOf.
   */
  getValue?: (item: unknown) => string | string[];
}

export interface FreeTextIdentityConfig<T> {
  idField: string;
  nameField: string;
  items: T[];
  matchFn: (item: T, text: string) => boolean;
  templateFn: (item: T) => string;
  groupFields?: GroupFieldDef[];
  getIdFromItem: (item: T) => number;
  /**
   * Per-row extra meta string appended after the auto-detected City/District/
   * Address line in the suggestion popover. Use this to surface filter-specific
   * identifiers that aren't on the standard cache shape: e.g. a Kiosk's
   * `ExternalReference`, or the aggregated kiosk references for a Location.
   */
  extraMeta?: (item: T) => string | undefined;
}

interface FreeTextIdentityFilterProps<T> {
  config: FreeTextIdentityConfig<T>;
  columnName: string;
  isParameter: boolean;
  value: InputParam[];
  onChange: (value: InputParam[]) => void;
  onSearch: () => void;
}

/**
 * Dispatcher for the Kiosk / Location / Product identity filters.
 *
 * Renders one of three layouts based on the user's "Filter display" preference
 * (user menu → Filter display): Compact pills (default) / Inline fields /
 * Command palette. All three emit the same identity-string InputParam so the
 * backend contract is unchanged.
 */
export function FreeTextIdentityFilter<T>(props: FreeTextIdentityFilterProps<T>) {
  const [mode] = useFilterDisplay();
  if (mode === "inline") return <InlineVariant {...props} />;
  if (mode === "palette") return <PaletteVariant {...props} />;
  return <CompactVariant {...props} />;
}
